import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Sparkles, CornerDownLeft } from 'lucide-react';
import { Language } from '../types';

interface TypewriterMemoryModeProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (text: string) => void;
  onFirstType?: () => void;
  language: Language;
}

const TRANSLATIONS: Record<Language, {
  placeholder: string;
  title: string;
  subtitle: string;
  save: string;
  hint: string;
  carriageReturn: string;
}> = {
  Italian: {
    placeholder: 'Scrivi il tuo ricordo...',
    title: 'Un ricordo',
    subtitle: 'Cattura questo momento',
    save: 'Salva',
    hint: 'Premi Invio per salvare',
    carriageReturn: 'A capo'
  },
  English: {
    placeholder: 'Write your memory...',
    title: 'A memory',
    subtitle: 'Capture this moment',
    save: 'Save',
    hint: 'Press Enter to save',
    carriageReturn: 'Return'
  },
  Spanish: {
    placeholder: 'Escribe tu recuerdo...',
    title: 'Un recuerdo',
    subtitle: 'Captura este momento',
    save: 'Guardar',
    hint: 'Presiona Enter para guardar',
    carriageReturn: 'Retorno'
  },
  German: {
    placeholder: 'Schreibe deine Erinnerung...',
    title: 'Eine Erinnerung',
    subtitle: 'Halte diesen Moment fest',
    save: 'Speichern',
    hint: 'Drücke Enter zum Speichern',
    carriageReturn: 'Zeilenumbruch'
  },
  French: {
    placeholder: 'Écris ton souvenir...',
    title: 'Un souvenir',
    subtitle: 'Capture ce moment',
    save: 'Sauvegarder',
    hint: 'Appuie sur Entrée pour sauvegarder',
    carriageReturn: 'Retour'
  }
};

// Costanti per l'animazione del carrello
const MAX_CARRIAGE_POSITION = 240; // Posizione massima a destra
const TYPING_SPEED = 6; // Pixel per carattere (movimento verso destra)
const RETURN_DURATION = 400; // Durata animazione ritorno in ms

// Funzione per riprodurre il suono "ding" della macchina da scrivere
const playDingSound = () => {
  try {
    const audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();

    // Oscillatore per il tono principale (campanella)
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Frequenza tipica di una campanella di macchina da scrivere
    oscillator.frequency.setValueAtTime(2200, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(1800, audioContext.currentTime + 0.1);

    oscillator.type = 'sine';

    // Envelope: attacco veloce, decadimento graduale
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);

    // Secondo tono più alto per effetto metallico
    const oscillator2 = audioContext.createOscillator();
    const gainNode2 = audioContext.createGain();

    oscillator2.connect(gainNode2);
    gainNode2.connect(audioContext.destination);

    oscillator2.frequency.setValueAtTime(3500, audioContext.currentTime);
    oscillator2.frequency.exponentialRampToValueAtTime(2800, audioContext.currentTime + 0.08);
    oscillator2.type = 'sine';

    gainNode2.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode2.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + 0.005);
    gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator2.start(audioContext.currentTime);
    oscillator2.stop(audioContext.currentTime + 0.3);
  } catch {
    // Audio non supportato, continua silenziosamente
  }
};

const TypewriterMemoryMode: React.FC<TypewriterMemoryModeProps> = ({
  isOpen,
  onClose,
  onSave,
  onFirstType,
  language
}) => {
  const [text, setText] = useState('');
  const [displayText, setDisplayText] = useState('');
  const [isExiting, setIsExiting] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(true);
  const [carriagePosition, setCarriagePosition] = useState(0);
  const [isCarriageReturning, setIsCarriageReturning] = useState(false);
  const [showDing, setShowDing] = useState(false);
  const [hasTyped, setHasTyped] = useState(false);
  const [keyPressEffect, setKeyPressEffect] = useState(false);
  const [leverPressed, setLeverPressed] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const displayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const carriageAnimationRef = useRef<number | null>(null);
  const currentLineLengthRef = useRef(0);

  const t = TRANSLATIONS[language] || TRANSLATIONS.English;

  // Cursore lampeggiante
  useEffect(() => {
    const interval = setInterval(() => {
      setCursorVisible(prev => !prev);
    }, 530);
    return () => clearInterval(interval);
  }, []);

  // Focus automatico all'apertura
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Reset quando si chiude
  useEffect(() => {
    if (!isOpen) {
      setText('');
      setDisplayText('');
      setIsExiting(false);
      setCarriagePosition(0);
      setIsCarriageReturning(false);
      setShowDing(false);
      setHasTyped(false);
      setKeyPressEffect(false);
      setLeverPressed(false);
      currentLineLengthRef.current = 0;
      if (carriageAnimationRef.current) {
        cancelAnimationFrame(carriageAnimationRef.current);
        carriageAnimationRef.current = null;
      }
    }
  }, [isOpen]);

  // Funzione per il carriage return con animazione
  const triggerCarriageReturn = useCallback(() => {
    if (isCarriageReturning) return;

    // Riproduci suono ding
    playDingSound();

    // Mostra ding visivo
    setShowDing(true);
    setLeverPressed(true);

    // Animazione leva
    setTimeout(() => setLeverPressed(false), 200);
    setTimeout(() => setShowDing(false), 1000);

    // Cancella animazione precedente
    if (carriageAnimationRef.current) {
      cancelAnimationFrame(carriageAnimationRef.current);
    }

    setIsCarriageReturning(true);

    const startPosition = carriagePosition;
    const startTime = performance.now();

    const animateReturn = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / RETURN_DURATION, 1);

      // Easing: veloce all'inizio, rallenta alla fine con piccolo rimbalzo
      let eased: number;
      if (progress < 0.7) {
        // Fase veloce
        eased = 1 - Math.pow(1 - (progress / 0.7), 2);
      } else {
        // Piccolo rimbalzo
        const bounceProgress = (progress - 0.7) / 0.3;
        const bounce = Math.sin(bounceProgress * Math.PI) * 0.05;
        eased = 1 + bounce;
      }

      const newPosition = startPosition * (1 - Math.min(eased, 1));
      setCarriagePosition(Math.max(0, newPosition));

      if (progress < 1) {
        carriageAnimationRef.current = requestAnimationFrame(animateReturn);
      } else {
        setCarriagePosition(0);
        currentLineLengthRef.current = 0;
        carriageAnimationRef.current = null;
        // Piccolo delay prima di permettere nuove scritture
        setTimeout(() => {
          setIsCarriageReturning(false);
        }, 50);
      }
    };

    carriageAnimationRef.current = requestAnimationFrame(animateReturn);
  }, [carriagePosition, isCarriageReturning]);

  // Gestione input testo
  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    const oldText = text;
    const isAdding = newText.length > oldText.length;

    setText(newText);

    // Effetto pressione tasto
    if (isAdding) {
      setKeyPressEffect(true);
      setTimeout(() => setKeyPressEffect(false), 80);
    }

    // Prima digitazione
    if (!hasTyped && newText.length > 0) {
      setHasTyped(true);
      onFirstType?.();
    }

    // Calcola lunghezza linea corrente
    const lines = newText.split('\n');
    const currentLine = lines[lines.length - 1];
    const newLineLength = currentLine.length;

    // Se sta cancellando, aggiorna posizione immediatamente
    if (!isAdding && !isCarriageReturning) {
      const newPosition = Math.min(newLineLength * TYPING_SPEED, MAX_CARRIAGE_POSITION);
      setCarriagePosition(newPosition);
      currentLineLengthRef.current = newLineLength;
    }
    // Se sta scrivendo e non sta tornando
    else if (isAdding && !isCarriageReturning) {
      // Movimento fluido verso destra
      const targetPosition = Math.min(newLineLength * TYPING_SPEED, MAX_CARRIAGE_POSITION);

      // Animazione fluida del movimento
      if (carriageAnimationRef.current) {
        cancelAnimationFrame(carriageAnimationRef.current);
      }

      const startPosition = carriagePosition;
      const distance = targetPosition - startPosition;
      const startTime = performance.now();
      const duration = 80; // ms per ogni carattere

      const animateTyping = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Easing fluido
        const eased = 1 - Math.pow(1 - progress, 3);

        const newPosition = startPosition + (distance * eased);
        setCarriagePosition(newPosition);

        if (progress < 1) {
          carriageAnimationRef.current = requestAnimationFrame(animateTyping);
        } else {
          carriageAnimationRef.current = null;
        }
      };

      carriageAnimationRef.current = requestAnimationFrame(animateTyping);
      currentLineLengthRef.current = newLineLength;
    }

    // Aggiorna display text con piccolo delay per effetto typewriter
    if (isAdding) {
      if (displayTimeoutRef.current) {
        clearTimeout(displayTimeoutRef.current);
      }
      displayTimeoutRef.current = setTimeout(() => {
        setDisplayText(newText);
      }, 20);
    } else {
      setDisplayText(newText);
    }
  }, [text, carriagePosition, isCarriageReturning, hasTyped, onFirstType]);

  // Gestione pulsante carriage return
  const handleCarriageReturnClick = useCallback(() => {
    if (isCarriageReturning) return;

    // Inserisci a capo nel testo
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newText = text.substring(0, start) + '\n' + text.substring(end);
      setText(newText);
      setDisplayText(newText);

      // Sposta cursore dopo il newline
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 1;
        textarea.focus();
      }, 50);
    }

    // Trigger animazione carriage return
    triggerCarriageReturn();
  }, [text, isCarriageReturning, triggerCarriageReturn]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && text.trim()) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      handleClose();
    }
  }, [text]);

  const handleSave = useCallback(() => {
    if (!text.trim()) return;

    setIsExiting(true);
    setTimeout(() => {
      onSave(text.trim());
      setText('');
      setDisplayText('');
      onClose();
    }, 400);
  }, [text, onSave, onClose]);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      setText('');
      setDisplayText('');
      onClose();
    }, 300);
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col transition-all duration-500 ease-out ${
        isExiting ? 'opacity-0' : 'opacity-100'
      }`}
      style={{
        background: 'linear-gradient(180deg, #E8DFD0 0%, #D8CFC0 50%, #C8BFB0 100%)'
      }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          opacity: 0.15,
          mixBlendMode: 'multiply'
        }}
      />

      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 0%, rgba(139, 119, 101, 0.15) 100%)'
        }}
      />

      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 31px, rgba(160,140,120,0.08) 31px, rgba(160,140,120,0.08) 32px)',
          backgroundPositionY: '140px'
        }}
      />

      <header
        className={`relative z-10 px-6 pt-14 pb-4 transition-all duration-700 ease-out ${
          isExiting ? 'opacity-0 -translate-y-4' : 'opacity-100 translate-y-0'
        }`}
        style={{ transitionDelay: '50ms' }}
      >
        <div className="flex items-center justify-between max-w-xl mx-auto">
          <button
            onClick={handleClose}
            className="w-10 h-10 rounded-full bg-stone-800/10 flex items-center justify-center transition-all duration-200 active:scale-90 hover:bg-stone-800/20 border border-stone-600/20"
          >
            <X size={20} className="text-stone-700" />
          </button>

          <div className="text-center">
            <h1
              className="text-xl font-bold text-stone-800 tracking-wider uppercase"
              style={{ 
                fontFamily: "'Special Elite', 'Courier New', Courier, monospace",
                textShadow: '1px 1px 0 rgba(255,255,255,0.5)'
              }}
            >
              {t.title}
            </h1>
            <p 
              className="text-xs text-stone-600 mt-1 tracking-wide"
              style={{ fontFamily: "'Special Elite', 'Courier New', Courier, monospace" }}
            >
              {t.subtitle}
            </p>
          </div>

          <button
            onClick={handleSave}
            disabled={!text.trim()}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 border ${
              text.trim()
                ? 'bg-stone-800 text-amber-50 shadow-lg shadow-stone-800/30 hover:bg-stone-900 active:scale-90 border-stone-900'
                : 'bg-stone-300/50 text-stone-400 border-stone-400/30'
            }`}
          >
            <Send size={16} />
          </button>
        </div>
      </header>

      <div
        className={`relative mx-auto mt-2 mb-2 transition-all duration-700 ease-out ${
          isExiting ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
        } ${keyPressEffect ? 'typewriter-machine-shake' : ''}`}
        style={{ transitionDelay: '100ms' }}
      >
        <div className="relative flex items-center justify-center gap-3">
          {/* Macchina da scrivere */}
          <div className="relative w-72">
            {/* Campanella */}
            <div className={`absolute -top-1 left-6 w-5 h-5 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 shadow-lg border-2 border-amber-600/50 flex items-center justify-center z-20 ${showDing ? 'bell-shake' : ''}`}>
              <div className={`w-2 h-2 rounded-full bg-amber-200 ${showDing ? 'animate-ping' : ''}`} />
            </div>

            {/* Corpo superiore macchina */}
            <div className="relative h-8 bg-gradient-to-b from-stone-600 via-stone-700 to-stone-800 rounded-t-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.3)] overflow-hidden border-t border-stone-500/30">
              {/* Guida carrello */}
              <div className="absolute inset-x-0 top-1.5 h-5 bg-gradient-to-b from-stone-800/80 to-stone-900/80 mx-3 rounded-full shadow-inner" />
              {/* Linea guida */}
              <div className="absolute inset-x-0 top-2.5 h-3 mx-4 flex items-center">
                <div className="w-full h-0.5 bg-stone-500/30 rounded-full" />
              </div>
              <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-t from-stone-900 to-transparent" />
            </div>

            {/* Barra con tacche */}
            <div className="relative h-3 bg-gradient-to-b from-stone-700 to-stone-800 flex items-center justify-center gap-0.5 px-4 shadow-inner rounded-b-sm">
              {[...Array(24)].map((_, i) => (
                <div key={i} className="w-0.5 h-1.5 bg-stone-500/40 rounded-full" />
              ))}
            </div>

            {/* Barretta rossa indicatore (si muove da sinistra verso destra) */}
            <div
              className={`absolute top-0 h-full w-1.5 z-10 rounded-full transition-shadow duration-200 ${isCarriageReturning ? 'carriage-return-glow' : ''}`}
              style={{
                left: `${16 + carriagePosition}px`,
                background: 'linear-gradient(to right, #dc2626, #ef4444, #dc2626)',
                boxShadow: isCarriageReturning
                  ? '0 0 12px rgba(239, 68, 68, 0.8), 0 0 20px rgba(239, 68, 68, 0.4)'
                  : '0 0 6px rgba(239, 68, 68, 0.5), inset 0 1px 2px rgba(255,255,255,0.3)'
              }}
            />

            {/* Notifica DING */}
            {showDing && (
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 animate-in fade-in zoom-in duration-200 z-30">
                <div className="bg-amber-50 text-amber-800 text-xs font-bold px-3 py-1 rounded-full shadow-xl border-2 border-amber-300 flex items-center gap-1 ding-bounce whitespace-nowrap">
                  <span className="text-sm bell-ring">🔔</span>
                  <span style={{ fontFamily: "'Special Elite', monospace" }}>DING!</span>
                </div>
              </div>
            )}
          </div>

          {/* Leva Carriage Return */}
          <button
            onClick={handleCarriageReturnClick}
            disabled={isCarriageReturning}
            className={`relative flex flex-col items-center gap-0.5 transition-all duration-200 active:scale-95 ${isCarriageReturning ? 'opacity-50' : 'hover:scale-105'}`}
            title={t.carriageReturn}
          >
            {/* Braccio leva */}
            <div
              className={`w-3 h-10 rounded-full shadow-lg transition-transform duration-200 ${leverPressed ? 'translate-y-1' : ''}`}
              style={{
                background: 'linear-gradient(to right, #78716c, #a8a29e, #78716c)',
                boxShadow: leverPressed
                  ? 'inset 0 2px 4px rgba(0,0,0,0.4)'
                  : '0 2px 4px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.3)'
              }}
            />
            {/* Pomello */}
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg transition-transform duration-200 ${leverPressed ? 'scale-95' : ''}`}
              style={{
                background: 'linear-gradient(145deg, #dc2626, #b91c1c)',
                boxShadow: leverPressed
                  ? 'inset 0 2px 6px rgba(0,0,0,0.5)'
                  : '0 3px 8px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
              }}
            >
              <CornerDownLeft size={14} className="text-white/90" />
            </div>
            {/* Etichetta */}
            <span
              className="text-[9px] text-stone-500 uppercase tracking-wider mt-0.5"
              style={{ fontFamily: "'Special Elite', monospace" }}
            >
              {t.carriageReturn}
            </span>
          </button>
        </div>
      </div>

      <main
        className={`flex-1 relative overflow-hidden transition-all duration-700 ease-out ${
          isExiting ? 'opacity-0 translate-y-8' : 'opacity-100 translate-y-0'
        }`}
        style={{ transitionDelay: '150ms' }}
      >
        <div className="h-full max-w-xl mx-auto px-6 py-4 overflow-y-auto">
          <div
            className={`relative min-h-full rounded-sm p-8 pb-24 ${keyPressEffect ? 'paper-press' : ''}`}
            style={{
              background: 'linear-gradient(135deg, #FDF8F0 0%, #F8F2E8 50%, #F5EFE5 100%)',
              boxShadow: `
                0 4px 6px -1px rgba(0,0,0,0.08),
                0 20px 40px -15px rgba(0,0,0,0.2),
                inset 0 1px 0 rgba(255,255,255,0.8),
                inset 2px 0 8px rgba(139,119,101,0.1),
                inset -2px 0 8px rgba(139,119,101,0.1)
              `,
              borderLeft: '1px solid rgba(139,119,101,0.15)',
              borderRight: '1px solid rgba(139,119,101,0.15)'
            }}
          >
            <div 
              className="absolute top-0 left-0 right-0 h-4 pointer-events-none"
              style={{
                background: 'linear-gradient(to bottom, rgba(139,119,101,0.1) 0%, transparent 100%)'
              }}
            />
            
            <div 
              className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none"
              style={{
                background: 'linear-gradient(to top, rgba(139,119,101,0.08) 0%, transparent 100%)'
              }}
            />

            <div className="absolute top-0 bottom-0 left-14 w-px bg-gradient-to-b from-red-400/50 via-red-400/60 to-red-400/40" />
            <div className="absolute top-0 bottom-0 left-[57px] w-px bg-red-400/20" />

            <div
              className="relative min-h-[300px] leading-8 whitespace-pre-wrap break-words pl-4 typewriter-text"
              style={{
                fontFamily: "'Special Elite', 'Courier New', Courier, monospace",
                letterSpacing: '0.03em',
                color: 'rgba(40, 35, 30, 0.95)',
                textShadow: '0.5px 0.5px 0px rgba(0,0,0,0.12), -0.3px -0.3px 0px rgba(255,255,255,0.4)'
              }}
            >
              {displayText}
              <span
                className={`inline-block w-2.5 h-6 ml-0.5 -mb-1 transition-opacity duration-100 ${
                  cursorVisible ? 'opacity-100' : 'opacity-0'
                }`}
                style={{
                  background: 'linear-gradient(to bottom, #1a1a1a 0%, #2d2d2d 100%)',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
                }}
              />
            </div>

            <textarea
              ref={textareaRef}
              value={text}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
              placeholder={t.placeholder}
              className="absolute inset-0 w-full h-full opacity-0 cursor-text resize-none p-8 pl-12"
              style={{
                fontFamily: "'Special Elite', 'Courier New', Courier, monospace",
                fontSize: '18px',
                lineHeight: '32px'
              }}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="sentences"
              spellCheck="false"
            />

            {!text && (
              <div
                className="absolute top-8 left-12 right-8 text-stone-400/60 text-lg pointer-events-none pl-4"
                style={{
                  fontFamily: "'Special Elite', 'Courier New', Courier, monospace",
                  letterSpacing: '0.03em'
                }}
              >
                {t.placeholder}
              </div>
            )}
          </div>
        </div>
      </main>

      <footer
        className={`relative z-10 px-6 pb-8 pt-2 transition-all duration-500 ease-out ${
          isExiting ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
        }`}
        style={{ transitionDelay: '200ms' }}
      >
        <div 
          className="flex items-center justify-center gap-2 text-stone-600 text-xs"
          style={{ fontFamily: "'Special Elite', 'Courier New', Courier, monospace" }}
        >
          <Sparkles size={12} className="text-amber-600/70" />
          <span>{t.hint}</span>
        </div>
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Special+Elite&display=swap');

        @keyframes typewriter-machine-shake {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(0.5px); }
        }

        .typewriter-machine-shake {
          animation: typewriter-machine-shake 0.05s ease-in-out;
        }

        @keyframes paper-press {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(0.999); }
        }

        .paper-press {
          animation: paper-press 0.06s ease-in-out;
        }

        @keyframes new-char {
          0% { opacity: 0.5; transform: scale(1.1); }
          100% { opacity: 1; transform: scale(1); }
        }

        .new-char {
          animation: new-char 0.1s ease-out;
        }

        @keyframes ding-bounce {
          0%, 100% { transform: scale(1) rotate(0deg); }
          25% { transform: scale(1.1) rotate(-2deg); }
          50% { transform: scale(1.05) rotate(2deg); }
          75% { transform: scale(1.08) rotate(-1deg); }
        }

        .ding-bounce {
          animation: ding-bounce 0.4s ease-out;
        }

        @keyframes bell-ring {
          0%, 100% { transform: rotate(0deg); }
          10% { transform: rotate(14deg); }
          20% { transform: rotate(-12deg); }
          30% { transform: rotate(10deg); }
          40% { transform: rotate(-8deg); }
          50% { transform: rotate(6deg); }
          60% { transform: rotate(-4deg); }
          70% { transform: rotate(2deg); }
          80% { transform: rotate(-1deg); }
          90% { transform: rotate(0.5deg); }
        }

        .bell-ring {
          display: inline-block;
          animation: bell-ring 0.6s ease-out;
          transform-origin: top center;
        }

        @keyframes bell-shake {
          0%, 100% { transform: translateX(0) rotate(0deg); }
          10% { transform: translateX(-2px) rotate(-5deg); }
          20% { transform: translateX(2px) rotate(5deg); }
          30% { transform: translateX(-2px) rotate(-3deg); }
          40% { transform: translateX(2px) rotate(3deg); }
          50% { transform: translateX(-1px) rotate(-2deg); }
          60% { transform: translateX(1px) rotate(2deg); }
          70% { transform: translateX(-1px) rotate(-1deg); }
          80% { transform: translateX(1px) rotate(1deg); }
          90% { transform: translateX(0) rotate(0deg); }
        }

        .bell-shake {
          animation: bell-shake 0.5s ease-out;
        }

        @keyframes carriage-glow {
          0%, 100% {
            box-shadow: 0 0 6px rgba(239, 68, 68, 0.5);
          }
          50% {
            box-shadow: 0 0 16px rgba(239, 68, 68, 0.9), 0 0 24px rgba(239, 68, 68, 0.5);
          }
        }

        .carriage-return-glow {
          animation: carriage-glow 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default TypewriterMemoryMode;
